import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { gql } from "generated-graphql";
import {
  FacilityPilotProgramQuery,
  FacilityPilotProgramReadyStatus,
} from "generated-graphql/graphql";
import { useCallback } from "react";
import { useForm, Controller } from "react-hook-form";

const FACILITY_PILOT_PROGRAM_QUERY = gql(`
  query FacilityPilotProgram($state: String!, $reportingYear: Int!) {
    facilityPilotProgram(state: $state, reportingYear: $reportingYear) {
      id
      state
      reportingYear
      status
      notes
      documentationComplete
      qaComplete
      createdAt
      createdBy
    }
  }
`);

const UPSERT_FACILITY_PILOT_PROGRAM_MUTATION = gql(`
  mutation UpsertFacilityPilotProgram($input: UpsertFacilityPilotProgramInput!) {
    upsertFacilityPilotProgram(input: $input) {
      id
      state
      reportingYear
      status
      notes
      documentationComplete
      qaComplete
      createdAt
      createdBy
    }
  }
`);

interface EditPilotProgramModalProps {
  open: boolean;
  onClose: () => void;
  state: string;
  reportingYear: number;
  onSave: () => void;
}

type FormData = {
  documentationComplete: boolean;
  qaComplete: boolean;
  status: FacilityPilotProgramReadyStatus;
  notes: string;
};

export function EditPilotProgramModal({
  open,
  onClose,
  state,
  reportingYear,
  onSave,
}: EditPilotProgramModalProps) {
  const alerts = useAlerts();
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      documentationComplete: false,
      qaComplete: false,
      status: FacilityPilotProgramReadyStatus.NotReady,
      notes: "",
    },
  });

  const { data, loading: queryLoading } = useQuery<FacilityPilotProgramQuery>(
    FACILITY_PILOT_PROGRAM_QUERY,
    {
      variables: { state, reportingYear },
      skip: !open,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.facilityPilotProgram) {
          reset({
            documentationComplete: data.facilityPilotProgram.documentationComplete ?? false,
            qaComplete: data.facilityPilotProgram.qaComplete ?? false,
            status: data.facilityPilotProgram.status ?? FacilityPilotProgramReadyStatus.NotReady,
            notes: data.facilityPilotProgram.notes ?? "",
          });
        } else {
          // No record exists, use defaults
          reset({
            documentationComplete: false,
            qaComplete: false,
            status: FacilityPilotProgramReadyStatus.NotReady,
            notes: "",
          });
        }
      },
    }
  );

  const [upsertMutation, { loading: saveLoading }] = useMutation(
    UPSERT_FACILITY_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Pilot program settings saved");
        onSave();
        onClose();
      },
      onError: (error) => {
        alerts.error(`Failed to save: ${error.message}`);
      },
    }
  );

  const onSubmit = useCallback(
    (formData: FormData) => {
      upsertMutation({
        variables: {
          input: {
            state,
            reportingYear,
            status: formData.status,
            notes: formData.notes || null,
            documentationComplete: formData.documentationComplete,
            qaComplete: formData.qaComplete,
          },
        },
      });
    },
    [upsertMutation, state, reportingYear]
  );

  const handleClose = useCallback(() => {
    if (!saveLoading) {
      reset();
      onClose();
    }
  }, [saveLoading, reset, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`Edit Pilot Program - ${state}`}
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        loading: saveLoading,
        onClick: handleSubmit(onSubmit),
        disabled: queryLoading,
      }}
    >
      <Box sx={{ pt: 1, px: 3, pb: 3 }}>
        <Stack spacing={2}>
          <RadioGroupField
            name="status"
            control={control}
            label="Ready"
            subtitle="This state is ready to be turned on, don't worry about the status of its pilot facilities"
            radioOptions={[
              {
                value: FacilityPilotProgramReadyStatus.Ready,
                label: "Ready",
              },
              {
                value: FacilityPilotProgramReadyStatus.NotReady,
                label: "Not Ready",
              },
            ]}
          />

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              State Checklist
            </FormLabel>
            <FormGroup sx={{ mb: 3 }}>
              <Controller
                name="qaComplete"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="QA is complete"
                  />
                )}
              />
              <Controller
                name="documentationComplete"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Documentation is complete"
                  />
                )}
              />
            </FormGroup>
          </FormControl>

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder="Add notes about this pilot program..."
              />
            )}
          />
        </Stack>
      </Box>
    </Dialog>
  );
}
